import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  Stack,
  Grid,
  Paper,
  IconButton,
  Tooltip,
  useTheme,
  Snackbar,
  Avatar,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  DataObject as DataObjectIcon,
  VerifiedUser as VerifiedUserIcon,
  WarningAmber as WarningAmberIcon,
  Add as AddIcon,
  Schedule as ScheduleIcon,
  TrackChanges as RequestIcon,
  Web as WebIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from "@mui/icons-material";
import { Alert as MuiAlert } from "@mui/material";
import dataService, {
  DataElementDetail,
  DataElementTimelineEvent,
  DataElementRequestSummary,
  DataElementRequestType,
} from "../services/data-service";
import { personalDataService } from "../services/personal-data-service";
import { RequestDialog } from "../components/RequestDialog";
import { config } from "../config/config";

const DataElementDetailPage: React.FC = () => {
  const { dataElementId } = useParams<{ dataElementId: string }>();
  const navigate = useNavigate();
  const theme = useTheme();

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [detail, setDetail] = useState<DataElementDetail | null>(null);
  const [revealedValue, setRevealedValue] = useState<string | null>(null);
  const [revealing, setRevealing] = useState<boolean>(false);
  const [requestOpen, setRequestOpen] = useState<boolean>(false);
  const [requestSubmitting, setRequestSubmitting] = useState<boolean>(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: "success" | "error" }>({
    open: false,
    message: "",
    severity: "success",
  });

  const loadDetail = useCallback(async () => {
    if (!dataElementId) {
      setError("Data element ID is required");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await dataService.getDataElementDetail(parseInt(dataElementId));
      setDetail(data);
    } catch (err: any) {
      setError(err?.message || "Failed to load data element details");
    } finally {
      setLoading(false);
    }
  }, [dataElementId]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  const handleBackClick = () => {
    navigate("/data");
  };

  const handleRevealValue = async () => {
    if (!detail || !detail.latestVaultId || !detail.latestConsentHeaderId) return;

    try {
      setRevealing(true);
      const response = await personalDataService.getPersonalDataValues(detail.latestConsentHeaderId, [detail.latestVaultId.toString()]);
      if (response.success) {
        const dataItem = personalDataService.getVaultPersonalData(response, detail.latestVaultId.toString());
        setRevealedValue(dataItem?.originalValue ?? null);
      } else {
        setSnackbar({ open: true, message: "Failed to reveal value", severity: "error" });
      }
    } catch (err: any) {
      setSnackbar({ open: true, message: err?.message || "Failed to reveal value", severity: "error" });
    } finally {
      setRevealing(false);
    }
  };

  const handleRequestSubmit = async (payload: { requestType: DataElementRequestType; description: string; reason?: string }) => {
    if (!detail) return;

    try {
      setRequestSubmitting(true);
      const response = await dataService.createDataElementRequest({
        requestType: payload.requestType,
        description: payload.description,
        reason: payload.reason,
        dataElementId: detail.dataElementId,
        dataElementKey: detail.dataElementKey,
      });

      setSnackbar({ open: true, message: "Request submitted successfully", severity: "success" });
      setRequestOpen(false);
      // Optimistic update: increment request counts and add to requests list
      if (detail && response.data) {
        const newRequest: DataElementRequestSummary = {
          requestId: response.data.requestId,
          requestType: payload.requestType,
          requestStatus: response.data.status,
          description: payload.description,
          reason: payload.reason || null,
          createdDate: response.data.submittedDate,
          estimatedCompletionDate: response.data.estimatedCompletionDate,
          actualCompletionDate: null,
        };
        setDetail({
          ...detail,
          totalRequests: detail.totalRequests + 1,
          openRequests: detail.openRequests + 1,
          requests: [newRequest, ...detail.requests],
          timeline: [
            {
              eventType: "REQUEST",
              eventDate: response.data.submittedDate,
              title: `${payload.requestType.replace(/_/g, " ")} request`,
              description: `Status: ${response.data.status}`,
              metadata: {
                requestId: response.data.requestId,
                description: payload.description,
                reason: payload.reason,
              },
            },
            ...detail.timeline,
          ],
        });
      }
    } catch (err: any) {
      setSnackbar({ open: true, message: err?.message || "Failed to submit request", severity: "error" });
    } finally {
      setRequestSubmitting(false);
    }
  };

  const formatDate = (value: string | null | undefined): string => {
    if (!value) return "—";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    return date.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getRequestStatusColor = (status: string): "default" | "primary" | "success" | "warning" | "error" => {
    switch (status.toUpperCase()) {
      case "COMPLETED":
        return "success";
      case "PENDING":
      case "IN_PROGRESS":
        return "warning";
      case "REJECTED":
      case "CANCELLED":
        return "error";
      default:
        return "default";
    }
  };

  const getRequestTypeLabel = (type: string): string => {
    return type.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !detail) {
    return (
      <Box sx={{ p: 3 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={handleBackClick} sx={{ mb: 2 }}>
          Back to Data
        </Button>
        <Alert severity="error">{error || "Data element not found"}</Alert>
      </Box>
    );
  }

  const isVaultProtected = detail.latestVaultId && detail.latestConsentHeaderId && !detail.latestValue;
  const displayValue = revealedValue !== null ? revealedValue : detail.latestValue;
  const showRevealButton = isVaultProtected && !revealedValue && !revealing;

  // Determine what to display: revealed value, direct value, "Protected" for vault, or "Not Available"
  const getDisplayText = (): string => {
    if (displayValue) return displayValue;
    if (detail.latestVaultId && detail.latestConsentHeaderId) return "Protected";
    return "Not Available";
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <IconButton onClick={handleBackClick} size="small">
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            {detail.elementName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Key: {detail.dataElementKey}
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setRequestOpen(true)} disabled={requestSubmitting}>
          New Request
        </Button>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={6} lg={3}>
          <Card>
            <CardContent>
              <Typography variant="caption" color="text.secondary" gutterBottom>
                Latest Value
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
                <Typography
                  variant="h6"
                  sx={{
                    flex: 1,
                    fontWeight: 600,
                    color: displayValue ? theme.palette.text.primary : theme.palette.text.disabled,
                  }}
                >
                  {getDisplayText()}
                </Typography>
                {showRevealButton && (
                  <Tooltip title="Reveal protected value">
                    <IconButton size="small" onClick={handleRevealValue} disabled={revealing}>
                      {revealing ? <CircularProgress size={20} /> : <VisibilityIcon fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                )}
                {revealedValue && (
                  <Tooltip title="Value revealed">
                    <VisibilityOffIcon fontSize="small" color="success" />
                  </Tooltip>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6} lg={3}>
          <Card>
            <CardContent>
              <Typography variant="caption" color="text.secondary" gutterBottom>
                Collection Points
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 600, mt: 1 }}>
                {detail.collectionPointCount}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6} lg={3}>
          <Card>
            <CardContent>
              <Typography variant="caption" color="text.secondary" gutterBottom>
                Total Requests
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 600, mt: 1 }}>
                {detail.totalRequests}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6} lg={3}>
          <Card>
            <CardContent>
              <Typography variant="caption" color="text.secondary" gutterBottom>
                Open Requests
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  mt: 1,
                  color: detail.openRequests > 0 ? theme.palette.warning.main : theme.palette.text.primary,
                }}
              >
                {detail.openRequests}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Element Info */}
      {detail.elementDescription && (
        <Card>
          <CardContent>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Description
            </Typography>
            <Typography variant="body1">{detail.elementDescription}</Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
              {detail.isIdentifier && <Chip icon={<VerifiedUserIcon sx={{ fontSize: 16 }} />} label="Identifier" size="small" color="primary" />}
              {detail.elementType && <Chip label={detail.elementType} size="small" variant="outlined" />}
              {detail.isPersonalData && <Chip label="Personal Data" size="small" variant="outlined" />}
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Timeline */}
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
            Timeline
          </Typography>
          {detail.timeline.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 4 }}>
              No timeline events available
            </Typography>
          ) : (
            <Box>
              {detail.timeline.map((event: DataElementTimelineEvent, index: number) => (
                <Box key={index} sx={{ display: "flex", mb: 3, position: "relative" }}>
                  {/* Timeline line */}
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mr: 2 }}>
                    <Avatar
                      sx={{
                        bgcolor: event.eventType === "COLLECTION" ? theme.palette.primary.main : theme.palette.secondary.main,
                        width: 40,
                        height: 40,
                      }}
                    >
                      {event.eventType === "COLLECTION" ? <DataObjectIcon fontSize="small" /> : <RequestIcon fontSize="small" />}
                    </Avatar>
                    {index < detail.timeline.length - 1 && (
                      <Box
                        sx={{
                          width: 2,
                          flex: 1,
                          bgcolor: theme.palette.divider,
                          minHeight: 40,
                          mt: 1,
                        }}
                      />
                    )}
                  </Box>
                  {/* Timeline content */}
                  <Box sx={{ flex: 1, pb: index < detail.timeline.length - 1 ? 0 : 0 }}>
                    <Paper sx={{ p: 2, bgcolor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.02)" }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          {event.title}
                        </Typography>
                        <Chip
                          label={event.eventType === "COLLECTION" ? "Collection" : "Request"}
                          size="small"
                          color={event.eventType === "COLLECTION" ? "primary" : "secondary"}
                        />
                      </Box>
                      {event.description && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {event.description}
                        </Typography>
                      )}
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 1 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <ScheduleIcon fontSize="small" color="action" />
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(event.eventDate)}
                          </Typography>
                        </Box>
                        {event.metadata?.collectionPointName && (
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <WebIcon fontSize="small" color="action" />
                            <Typography variant="caption" color="text.secondary">
                              {event.metadata.collectionPointName}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Paper>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Requests */}
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
            Associated Requests
          </Typography>
          {detail.requests.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 4 }}>
              No requests associated with this data element
            </Typography>
          ) : (
            <Stack spacing={2}>
              {detail.requests.map((request: DataElementRequestSummary) => (
                <Paper
                  key={request.requestId}
                  sx={{
                    p: 2,
                    bgcolor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.02)",
                    cursor: "pointer",
                    "&:hover": {
                      bgcolor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.04)",
                    },
                  }}
                  onClick={() => navigate(`/requests/${request.requestId}`)}
                >
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {getRequestTypeLabel(request.requestType)}
                      </Typography>
                      <Chip label={request.requestStatus} size="small" color={getRequestStatusColor(request.requestStatus)} />
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {formatDate(request.createdDate)}
                    </Typography>
                  </Box>
                  {request.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {request.description}
                    </Typography>
                  )}
                  {request.reason && (
                    <Typography variant="caption" color="text.secondary" sx={{ fontStyle: "italic" }}>
                      Reason: {request.reason}
                    </Typography>
                  )}
                  {request.estimatedCompletionDate && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 1 }}>
                      <ScheduleIcon fontSize="small" color="action" />
                      <Typography variant="caption" color="text.secondary">
                        Estimated completion: {formatDate(request.estimatedCompletionDate)}
                      </Typography>
                    </Box>
                  )}
                </Paper>
              ))}
            </Stack>
          )}
        </CardContent>
      </Card>

      {/* Request Dialog */}
      <RequestDialog
        open={requestOpen}
        onClose={() => setRequestOpen(false)}
        elementName={detail.elementName}
        elementKey={detail.dataElementKey}
        submitting={requestSubmitting}
        onSubmit={handleRequestSubmit}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={config.ui.snackbar.autoHideDuration}
        anchorOrigin={config.ui.snackbar.anchorOrigin}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <MuiAlert onClose={() => setSnackbar((s) => ({ ...s, open: false }))} severity={snackbar.severity} variant="filled" elevation={6}>
          {snackbar.message}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
};

export default DataElementDetailPage;
